<template>
  <v-dialog v-model="showCreateTagView" :width="isCompose ? '900px' : '600px'" persistent>
    <v-card :loading="loading"
      ><v-toolbar outlined>
        <span class="text-h6">{{ update ? 'Edit' : 'New' }} Tag</span>
        <v-spacer></v-spacer>

        <v-btn icon @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <!-- Edit Mode-->
      <div class="pt-4">
        <v-form lazy-validation v-model="valid" ref="tagForm">
          <v-card-text>
            <v-row>
              <v-col cols="12" class="p-0">
                <v-card-title class="py-1"> Name </v-card-title>
                <v-card-text>
                  <v-text-field
                    class="py-1"
                    v-model="changedTagName"
                    hide-details="auto"
                    clearable
                    prepend-inner-icon="mdi-link"
                    :rules="rules"
                    required
                    outlined
                    label="Name of your tag"
                  ></v-text-field>
                </v-card-text>
              </v-col>
              <v-col cols="12" class="p-0">
                <v-card-title class="py-1"> Label </v-card-title>
                <v-card-text>
                  <v-text-field
                    class="py-1"
                    v-model="tagLabel"
                    prepend-inner-icon="mdi-label-outline"
                    hide-details="auto"
                    clearable
                    outlined
                    label="Label for humans to understand"
                  ></v-text-field>
                </v-card-text>
              </v-col>
              <v-col cols="6" class="p-0">
                <v-card-title class="py-1"> Uncompressed Size </v-card-title>
                <v-card-text>
                  <v-text-field
                    class="py-1 inputPrice"
                    v-model.number="uncompressedSize"
                    hide-details="auto"
                    clearable
                    outlined
                    type="number"
                    label="Uncompressed size for tag (BYTE)"
                  ></v-text-field>
                </v-card-text>
              </v-col>

              <v-col cols="6" class="p-0">
                <v-card-title class="py-1"> Created At </v-card-title>
                <v-card-text>
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="createdAt"
                        label="Custom Date"
                        hint="YYYY-MM-DD format"
                        outlined
                        persistent-hint
                        append-icon="mdi-calendar"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="createdAt" no-title scrollable>
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.menu.save(createdAt)"
                      >
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-card-text>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-title class="py-1">CUDA Major (optional)</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="cudaMajor"
              outlined
              hide-details="auto"
              clearable
              prepend-inner-icon="mdi-chip"
              hint="Ex: 12 or 12.6 -- must exist in the CUDA Driver Map. On x86 it derives Min Driver Version; on Jetson it is checked against the L4T/CUDA matrix"
              persistent-hint
              label="CUDA version baked into this image"
            ></v-text-field>
          </v-card-text>

          <hr />

          <!--
            Compatibility is read from the image, not typed in. The detection service records
            what it observed in tag_image_facts; the resolver turns that into a device list at
            request time. Typing it by hand here meant re-entering something already known, and
            the two answers could disagree with nothing to reconcile them.
          -->
          <v-card-title class="py-1">
            Detected compatibility
            <v-spacer></v-spacer>
            <v-btn
              v-if="update"
              small text color="primary"
              :loading="scanning"
              :disabled="factsLoading"
              @click="scanNow"
              title="Read the published image now instead of waiting for the nightly run"
            >
              <v-icon small left>mdi-magnify-scan</v-icon>
              Scan now
            </v-btn>
            <v-btn icon small :loading="factsLoading" :disabled="scanning" @click="fetchCompat" title="Re-read">
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="scanMessage" dense text :type="scanMessageType" class="mb-2">
              {{ scanMessage }}
            </v-alert>
            <v-alert v-if="!update" dense text type="info" class="mb-0">
              The published image is scanned automatically as soon as the tag is saved; the
              result appears here when you open the tag again (usually within a minute).
            </v-alert>
            <v-alert v-else-if="scanning" dense text type="info" class="mb-0">
              Scanning the published image&hellip;
            </v-alert>
            <v-alert v-else-if="factsLoading" dense text type="info" class="mb-0">
              Reading&hellip;
            </v-alert>
            <v-alert v-else-if="!facts.length" dense text type="warning" class="mb-0">
              Nothing detected for this tag yet. Press "Scan now" to read the image, or use an
              override below to set compatibility by hand.
            </v-alert>
            <template v-else>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Platform</th>
                      <th class="text-left">Class</th>
                      <th class="text-left">GPU</th>
                      <th class="text-left">CUDA</th>
                      <th class="text-left">L4T</th>
                      <th class="text-left">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="f in facts" :key="f.platform">
                      <td>
                        {{ f.platform }}
                        <v-chip x-small v-if="f.publishes === false" color="grey" text-color="white">
                          not published
                        </v-chip>
                      </td>
                      <td>{{ f.platform_class || '-' }}</td>
                      <td>
                        {{ f.gpu_kind || '-' }}
                        <span v-if="f.gpu_backend && f.gpu_backend !== 'cuda'">
                          ({{ f.gpu_backend }})
                        </span>
                      </td>
                      <td>{{ f.cuda_version || '-' }}</td>
                      <td>{{ f.l4t_version || '-' }}</td>
                      <td>
                        <v-chip
                          x-small
                          :color="f.status === 'confirmed' ? 'green' : 'blue-grey'"
                          text-color="white"
                        >
                          {{ f.status }} {{ f.confidence }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-btn text x-small class="mt-2" @click="showEvidence = !showEvidence">
                {{ showEvidence ? 'Hide' : 'Show' }} evidence
              </v-btn>
              <v-card outlined v-if="showEvidence" class="mt-1 pa-2">
                <div v-for="f in facts" :key="'ev-' + f.platform" class="mb-2">
                  <strong>{{ f.platform }}</strong>
                  <div v-for="(e, i) in (f.evidence || [])" :key="i" class="text-caption">
                    {{ e.kind }} = {{ e.value }}
                    <span class="grey--text">via {{ e.source }} ({{ e.confidence }})</span>
                  </div>
                  <div v-for="(c, i) in (f.conflicts || [])" :key="'c' + i"
                       class="text-caption orange--text">
                    conflict: {{ c }}
                  </div>
                </div>
              </v-card>
            </template>
          </v-card-text>

          <hr />

          <!--
            An override is a deliberate exception, not the normal way to set compatibility. It
            lives in its own table so re-derivation cannot overwrite it -- previously the only
            way to make a human decision stick was is_controlled, which also froze the tag out
            of every future correction.
          -->
          <v-card-title class="py-1">
            Override
            <v-chip x-small class="ml-2" color="orange" text-color="white" v-if="hasOverride">
              active
            </v-chip>
          </v-card-title>
          <v-card-text>
            <v-alert dense text type="info" class="mb-3">
              Only for exceptions. Leave empty to use what was detected.
            </v-alert>

            <v-autocomplete
              outlined dense chips small-chips deletable-chips multiple clearable
              hide-details="auto" class="mb-3"
              label="Force these devices on"
              item-text="name"
              item-value="chipset_id"
              v-model="overrideAdd"
              :items="allDevicesAll"
            ></v-autocomplete>

            <v-autocomplete
              outlined dense chips small-chips deletable-chips multiple clearable
              hide-details="auto" class="mb-3"
              label="Hide on these devices"
              item-text="name"
              item-value="chipset_id"
              v-model="overrideDeny"
              :items="allDevicesAll"
            ></v-autocomplete>

            <v-select
              outlined dense clearable hide-details="auto" class="mb-3"
              label="Visibility"
              v-model="overrideVisibility"
              :items="visibilityChoices"
            ></v-select>

            <v-text-field
              outlined dense hide-details="auto" class="mb-3"
              label="Reason (optional)"
              hint="Why this tag is an exception. Left empty, the override is recorded as set by you."
              persistent-hint
              v-model="overrideReason"
            ></v-text-field>

            <v-btn small text color="error" v-if="hasOverride" @click="removeOverride">
              Remove override
            </v-btn>
          </v-card-text>

          <v-card-title class="py-1">Features</v-card-title>

          <v-card-text>
            <v-autocomplete
              outlined
              label="Tell us about what's inside of this environment"
              chips
              required
              :rules="arrayRules"
              item-text="text"
              item-value="id"
              hide-details="auto"
              clearable
              deletable-chips
              multiple
              small-chips
              v-model="changedSelectedVersions"
              :items="generateAllPossibleVersions"
            >
            </v-autocomplete>
          </v-card-text>

          <!-- Compose Services Builder -->
          <template v-if="isCompose">
            <hr />
            <v-card-title class="py-1">
              <v-icon left>mdi-docker</v-icon>
              Compose Services
              <v-spacer></v-spacer>
              <v-btn-toggle v-model="composeInputMode" mandatory dense class="mr-2">
                <v-btn value="visual" small outlined>
                  <v-icon left small>mdi-form-select</v-icon>Visual
                </v-btn>
                <v-btn value="raw" small outlined>
                  <v-icon left small>mdi-code-braces</v-icon>Raw YAML
                </v-btn>
              </v-btn-toggle>
              <v-btn v-if="composeInputMode === 'visual'" color="primary" small outlined @click="addComposeService">
                <v-icon left small>mdi-plus</v-icon>Add Service
              </v-btn>
            </v-card-title>

            <!-- Raw YAML Editor Mode -->
            <div v-if="composeInputMode === 'raw'" class="px-4 pb-2">
              <v-alert v-if="rawYamlError" type="error" dense dismissible class="mb-2">
                {{ rawYamlError }}
              </v-alert>
              <v-textarea
                v-model="rawYamlText"
                label="Paste your docker-compose.yml content here"
                outlined
                auto-grow
                :rows="12"
                class="raw-yaml-editor"
                placeholder="version: '3'\nservices:\n  web:\n    image: nginx:latest\n    ports:\n      - '8080:80'"
                style="font-family: monospace; font-size: 13px;"
                hide-details="auto"
              ></v-textarea>
              <div class="d-flex justify-end mt-2">
                <v-btn color="primary" small @click="applyRawYaml">
                  <v-icon left small>mdi-check</v-icon>Apply
                </v-btn>
              </div>
            </div>

            <!-- Visual Editor Mode -->
            <v-card-text v-if="composeInputMode === 'visual' && composeServices.length === 0" class="text-center grey--text">
              No services defined. Click "Add Service" to start building your compose file.
            </v-card-text>

            <v-expansion-panels v-model="expandedServicePanel" class="px-4 pb-2" v-if="composeInputMode === 'visual' && composeServices.length > 0">
              <v-expansion-panel v-for="(service, idx) in composeServices" :key="idx">
                <v-expansion-panel-header>
                  <div class="d-flex align-center" style="width: 100%">
                    <v-radio-group v-model="primaryServiceIndex" hide-details class="mt-0 pt-0 mr-2" @click.stop>
                      <v-radio :value="idx" class="mr-0"></v-radio>
                    </v-radio-group>
                    <v-icon left small :color="primaryServiceIndex === idx ? 'primary' : 'grey'">mdi-server</v-icon>
                    <span class="font-weight-medium">{{ service.name || `Service ${idx + 1}` }}</span>
                    <v-chip v-if="primaryServiceIndex === idx" x-small color="primary" class="ml-2">Primary</v-chip>
                    <v-spacer></v-spacer>
                    <v-btn icon x-small @click.stop="removeComposeService(idx)" color="red">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <!-- Service Name -->
                    <v-col cols="6">
                      <v-text-field
                        v-model="service.name"
                        label="Service Name *"
                        outlined dense hide-details="auto"
                        :rules="rules"
                        placeholder="e.g. web, redis, postgres"
                      ></v-text-field>
                    </v-col>
                    <!-- Image / Build -->
                    <v-col cols="6">
                      <v-text-field
                        v-model="service.image"
                        :label="service.buildContext ? 'Image (optional when build is set)' : 'Image *'"
                        outlined dense hide-details="auto"
                        :rules="service.buildContext ? [] : rules"
                        placeholder="e.g. nginx:latest"
                      ></v-text-field>
                    </v-col>
                    <!-- Build Context -->
                    <v-col cols="6">
                      <v-text-field
                        v-model="service.buildContext"
                        label="Build Context (optional)"
                        outlined dense hide-details
                        placeholder="e.g. . or ../.. or ./subdir"
                      ></v-text-field>
                    </v-col>
                    <!-- Dockerfile Path -->
                    <v-col cols="6" v-if="service.buildContext">
                      <v-text-field
                        v-model="service.buildDockerfile"
                        label="Dockerfile Path (optional)"
                        outlined dense hide-details
                        placeholder="e.g. Dockerfile or dev/containers/Dockerfile"
                      ></v-text-field>
                    </v-col>

                    <!-- Restart Policy -->
                    <v-col cols="6">
                      <v-select
                        v-model="service.restart"
                        :items="restartPolicies"
                        label="Restart Policy"
                        outlined dense hide-details
                      ></v-select>
                    </v-col>
                    <!-- Depends On -->
                    <v-col cols="6">
                      <v-select
                        v-model="service.depends_on"
                        :items="getOtherServiceNames(idx)"
                        label="Depends On"
                        outlined dense hide-details
                        multiple small-chips deletable-chips
                      ></v-select>
                    </v-col>

                    <!-- Command -->
                    <v-col cols="6">
                      <v-text-field
                        v-model="service.command"
                        label="Command"
                        outlined dense hide-details
                        placeholder="e.g. python app.py"
                      ></v-text-field>
                    </v-col>
                    <!-- Entrypoint -->
                    <v-col cols="6">
                      <v-text-field
                        v-model="service.entrypoint"
                        label="Entrypoint"
                        outlined dense hide-details
                        placeholder="e.g. /entrypoint.sh"
                      ></v-text-field>
                    </v-col>

                    <!-- Networks -->
                    <v-col cols="6">
                      <v-text-field
                        v-model="service.networks"
                        label="Networks (comma separated)"
                        outlined dense hide-details
                        placeholder="e.g. frontend, backend"
                      ></v-text-field>
                    </v-col>

                    <!-- Profiles -->
                    <v-col cols="6">
                      <v-text-field
                        v-model="service.profilesText"
                        label="Profiles (comma separated)"
                        outlined dense hide-details
                        placeholder="e.g. with-db, cloud"
                        hint="Leave empty for services that always run"
                        @input="syncServiceProfiles(service)"
                      ></v-text-field>
                    </v-col>

                    <!-- Resource Limits -->
                    <v-col cols="3">
                      <v-text-field
                        v-model="service.cpus"
                        label="CPU Limit"
                        outlined dense hide-details
                        placeholder="e.g. 2.0"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        v-model="service.memory"
                        label="Memory Limit"
                        outlined dense hide-details
                        placeholder="e.g. 512M"
                      ></v-text-field>
                    </v-col>

                    <!-- Environment Variables -->
                    <v-col cols="12">
                      <div class="d-flex align-center mb-1">
                        <span class="subtitle-2">Environment Variables</span>
                        <v-spacer></v-spacer>
                        <v-btn x-small text color="primary" @click="addEnvVar(service)">
                          <v-icon x-small left>mdi-plus</v-icon>Add
                        </v-btn>
                      </div>
                      <v-row dense v-for="(env, eIdx) in service.environment" :key="'env-'+eIdx">
                        <v-col cols="5">
                          <v-text-field v-model="env.key" label="Key" outlined dense hide-details placeholder="VAR_NAME"></v-text-field>
                        </v-col>
                        <v-col cols="5">
                          <v-text-field v-model="env.value" label="Value" outlined dense hide-details placeholder="value"></v-text-field>
                        </v-col>
                        <v-col cols="2" class="d-flex align-center">
                          <v-btn icon x-small @click="service.environment.splice(eIdx, 1)"><v-icon small>mdi-close</v-icon></v-btn>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Ports -->
                    <v-col cols="12">
                      <div class="d-flex align-center mb-1">
                        <span class="subtitle-2">Ports</span>
                        <v-spacer></v-spacer>
                        <v-btn x-small text color="primary" @click="addPort(service)">
                          <v-icon x-small left>mdi-plus</v-icon>Add
                        </v-btn>
                      </div>
                      <v-row dense v-for="(port, pIdx) in service.ports" :key="'port-'+pIdx">
                        <v-col cols="5">
                          <v-text-field v-model="port.host" label="Host Port" outlined dense hide-details placeholder="8080"></v-text-field>
                        </v-col>
                        <v-col cols="5">
                          <v-text-field v-model="port.container" label="Container Port" outlined dense hide-details placeholder="80"></v-text-field>
                        </v-col>
                        <v-col cols="2" class="d-flex align-center">
                          <v-btn icon x-small @click="service.ports.splice(pIdx, 1)"><v-icon small>mdi-close</v-icon></v-btn>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Volumes -->
                    <v-col cols="12">
                      <div class="d-flex align-center mb-1">
                        <span class="subtitle-2">Volumes</span>
                        <v-spacer></v-spacer>
                        <v-btn x-small text color="primary" @click="addVolume(service)">
                          <v-icon x-small left>mdi-plus</v-icon>Add
                        </v-btn>
                      </div>
                      <v-row dense v-for="(vol, vIdx) in service.volumes" :key="'vol-'+vIdx">
                        <v-col cols="5">
                          <v-text-field v-model="vol.host" label="Host Path" outlined dense hide-details placeholder="/host/path"></v-text-field>
                        </v-col>
                        <v-col cols="5">
                          <v-text-field v-model="vol.container" label="Container Path" outlined dense hide-details placeholder="/container/path"></v-text-field>
                        </v-col>
                        <v-col cols="2" class="d-flex align-center">
                          <v-btn icon x-small @click="service.volumes.splice(vIdx, 1)"><v-icon small>mdi-close</v-icon></v-btn>
                        </v-col>
                      </v-row>
                    </v-col>

                    <!-- Healthcheck -->
                    <v-col cols="12">
                      <v-expansion-panels flat>
                        <v-expansion-panel>
                          <v-expansion-panel-header class="pa-0">
                            <span class="subtitle-2">Healthcheck (optional)</span>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <v-row dense>
                              <v-col cols="12">
                                <v-text-field v-model="service.healthcheck.test" label="Test Command" outlined dense hide-details placeholder="CMD curl -f http://localhost/ || exit 1"></v-text-field>
                              </v-col>
                              <v-col cols="4">
                                <v-text-field v-model="service.healthcheck.interval" label="Interval" outlined dense hide-details placeholder="30s"></v-text-field>
                              </v-col>
                              <v-col cols="4">
                                <v-text-field v-model="service.healthcheck.timeout" label="Timeout" outlined dense hide-details placeholder="10s"></v-text-field>
                              </v-col>
                              <v-col cols="4">
                                <v-text-field v-model="service.healthcheck.retries" label="Retries" outlined dense hide-details placeholder="3" type="number"></v-text-field>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Repository Settings -->
            <div class="px-4 pb-2 mt-4">
              <div class="d-flex align-center mb-2">
                <v-icon left small>mdi-git</v-icon>
                <span class="subtitle-1 font-weight-medium">Repository Settings</span>
              </div>
              <v-alert type="info" dense text class="mb-2">
                If a Git repository URL is provided, the compose file will be cloned from the repository instead of pulling images directly. User-configured values will be merged on top of the cloned compose file.
              </v-alert>
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="composeRepoUrl"
                    label="Repository URL"
                    outlined dense hide-details="auto"
                    placeholder="https://github.com/org/repo.git"
                    prepend-inner-icon="mdi-link"
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    v-model="composeRepoBranch"
                    label="Branch / Tag"
                    outlined dense hide-details
                    placeholder="main"
                    prepend-inner-icon="mdi-source-branch"
                  ></v-text-field>
                </v-col>
                <v-col cols="3">
                  <v-text-field
                    v-model="composeRepoPath"
                    label="Subfolder Path (optional)"
                    outlined dense hide-details
                    placeholder="deploy/docker"
                    prepend-inner-icon="mdi-folder-outline"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="6">
                  <v-text-field
                    v-model="composeRepoFilename"
                    label="Compose Filename (optional)"
                    outlined dense hide-details
                    placeholder="docker-compose.selfhost.yml"
                    prepend-inner-icon="mdi-file-document-outline"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>

            <!-- User-Configurable Variables -->
            <div class="px-4 pb-2 mt-4">
              <div class="d-flex align-center mb-2">
                <v-icon left small>mdi-variable</v-icon>
                <span class="subtitle-1 font-weight-medium">User-Configurable Variables</span>
                <v-spacer></v-spacer>
                <v-btn small text color="primary" @click="addComposeVariable">
                  <v-icon left small>mdi-plus</v-icon>Add Variable
                </v-btn>
              </div>
              <v-alert v-if="composeVariables.length === 0" type="info" dense text class="mb-0">
                Define variables that end users can customize before starting. Use <code>{<!-- -->{VAR_NAME}}</code> syntax in environment values, commands, or volume paths.
              </v-alert>
              <v-card v-for="(cv, cvIdx) in composeVariables" :key="'cv-'+cvIdx" outlined class="mb-2 pa-3">
                <v-row dense>
                  <v-col cols="3">
                    <v-text-field
                      v-model="cv.key"
                      label="Variable Key *"
                      outlined dense hide-details="auto"
                      :rules="rules"
                      placeholder="API_KEY"
                      @input="cv.key = ($event || '').toUpperCase().replace(/[^A-Z0-9_]/g, '')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="cv.label"
                      label="Display Label *"
                      outlined dense hide-details="auto"
                      :rules="rules"
                      placeholder="API Key"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-select
                      v-model="cv.type"
                      :items="variableTypes"
                      label="Type"
                      outlined dense hide-details
                    ></v-select>
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      v-model="cv.default"
                      label="Default Value"
                      outlined dense hide-details
                      placeholder="optional"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-select
                      v-model="cv.services"
                      :items="composeServiceNames"
                      label="Target Services"
                      outlined dense hide-details
                      multiple
                      small-chips
                      deletable-chips
                      placeholder="All services"
                      clearable
                    >
                      <template v-slot:prepend-item>
                        <v-list-item @click="cv.services = [...composeServiceNames]">
                          <v-list-item-content>
                            <v-list-item-title class="caption font-weight-bold">Select All</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider></v-divider>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="1" class="d-flex align-center justify-space-between">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn icon x-small v-on="on" @click="cv.required = !cv.required" :color="cv.required ? 'red' : 'grey'">
                          <v-icon small>{{ cv.required ? 'mdi-asterisk' : 'mdi-asterisk' }}</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ cv.required ? 'Required' : 'Optional' }} — click to toggle</span>
                    </v-tooltip>
                    <v-btn icon x-small @click="composeVariables.splice(cvIdx, 1)" color="red">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </div>
          </template>

          <v-card-text class="justify-end d-flex">
            <v-btn
              color="primary"
              @click="saveData"
              :disabled="!valid || loading"
              :loading="loading"
            >
              {{ update ? 'Update' : 'Save' }}
            </v-btn>
          </v-card-text>
        </v-form>
      </div>
      <div></div>
    </v-card>
  </v-dialog>
</template>

<script>
import converter from '@/mixins/converter'
import _ from 'lodash'
import Swal from 'sweetalert2'
import yaml from 'js-yaml'
import { apiService } from '../../service/api.service'
export default converter.extend({
  data: () => ({
    menu: false,
    createdAt: null,
    tagsOverlay: false,
    zIndex: 0,
    changedJetpacks: [],
    changedTagName: null,
    tagLabel: null,
    uncompressedSize: null,
    propList: [],
    valid: true,
    minDriverVersion: null,
    cudaMajor: null,
    allDevices: [],
    allJetpacks: [],
    radios: null,
    // True while `radios` is being set from stored tag data, so the watcher can tell an
    // admin switching arch (which should clear the other side) from the dialog opening.
    settingRadios: false,
    changedSelectedVersions: [],
    selectedDevices: [],
    update: false,
    loading: false,
    // Compatibility is read, not typed. These hold what detection found and what a human
    // deliberately overrode; neither is edited into the tag's own fields any more.
    facts: [],
    factsLoading: false,
    showEvidence: false,
    hasOverride: false,
    overrideAdd: [],
    overrideDeny: [],
    overrideVisibility: null,
    overrideReason: '',
    scanning: false,
    scanMessage: '',
    scanMessageType: 'info',
    allDevicesAll: [],
    visibilityChoices: [
      { text: 'Use what was detected', value: null },
      { text: 'Always show', value: 'force_show' },
      { text: 'Always hide', value: 'force_hide' }
    ],
    rules: [(v) => !!v || 'Field is required'],
    arrayRules: [(v) => v.length > 0 || 'Field is required'],
    // Compose fields
    composeServices: [],
    composeTopLevelKeys: {},
    primaryServiceIndex: 0,
    expandedServicePanel: null,
    restartPolicies: ['no', 'always', 'on-failure', 'unless-stopped'],
    // Raw YAML editor
    composeInputMode: 'visual',
    rawYamlText: '',
    rawYamlError: null,
    // User-configurable variables
    composeVariables: [],
    // Compose repository fields
    composeRepoUrl: '',
    composeRepoBranch: '',
    composeRepoPath: '',
    composeRepoFilename: '',
    // Compose profiles (auto-extracted from services)
    composeProfiles: {},
    variableTypes: [
      { text: 'Text', value: 'text' },
      { text: 'Number', value: 'number' },
      { text: 'Password', value: 'password' },
      { text: 'URL', value: 'url' },
      { text: 'Port', value: 'port' }
    ]
  }),
  props: {
    showCreateTagView: Boolean,
    tag: {
      default: () => false
    },
    dockerId: {
      default: () => null
    },
    dockerType: {
      type: String,
      default: null
    }
  },

  methods: {
    /**
     * Read what detection observed for this tag, plus any human override.
     *
     * Two calls rather than one because they mean different things: the facts are what the
     * image says, the override is what a person decided about it. Keeping them apart on screen
     * is the point -- the previous form showed one set of fields and gave no way to tell which
     * was which.
     */
    async fetchCompat () {
      if (!this.update || !this.tag || !this.tag.id) {
        this.facts = []
        this.hasOverride = false
        this.overrideAdd = []
        this.overrideDeny = []
        this.overrideVisibility = null
        this.overrideReason = ''
        return
      }
      this.factsLoading = true
      try {
        const [factsRes, overrideRes] = await Promise.all([
          apiService.get(`admin/tag-facts/${this.tag.id}`),
          apiService.get(`admin/tag-compat-overrides/${this.tag.id}`)
        ])
        this.facts = factsRes.data.facts || []

        const o = overrideRes.data.override
        this.hasOverride = !!o
        this.overrideAdd = (o && o.chipset_ids_add) || []
        this.overrideDeny = (o && o.chipset_ids_deny) || []
        this.overrideVisibility = (o && o.visibility_override) || null
        this.overrideReason = (o && o.reason) || ''
      } catch (e) {
        this.facts = []
        this.hasOverride = false
      } finally {
        this.factsLoading = false
      }
    },

    /**
     * Ask the hub to read this tag's image now, then show what it stored.
     *
     * The call is synchronous on the backend, so when it returns the facts are already written;
     * the short poll only covers a scan someone else started (409) finishing a moment later.
     * An unreadable registry is reported explicitly -- those results are not stored, and would
     * otherwise look exactly like "nothing detected".
     */
    async scanNow () {
      if (!this.tag || !this.tag.id) { return }
      this.scanning = true
      this.scanMessage = ''
      let alreadyRunning = false
      try {
        const res = await apiService.post(`admin/tag-facts/${this.tag.id}/scan`, {})
        this.describeScan(res.data)
      } catch (err) {
        const status = err.response && err.response.status
        const body = (err.response && err.response.data) || {}
        if (status === 409) {
          alreadyRunning = true
          this.scanMessageType = 'info'
          this.scanMessage = 'A scan of this tag is already running; waiting for it.'
        } else if (body && body.per_tag) {
          this.describeScan(body)
        } else {
          this.scanMessageType = 'error'
          this.scanMessage = body.message || err.message || 'Scan failed.'
        }
      } finally {
        this.scanning = false
      }
      await this.fetchCompat()
      const polls = alreadyRunning ? 6 : 0
      for (let i = 0; i < polls && !this.facts.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 5000))
        await this.fetchCompat()
      }
      // The scan rebuilt this tag's device list; without this the list behind the dialog keeps
      // showing the one it loaded before the scan.
      this.$emit('tag-updated')
    },

    describeScan (result) {
      const own = ((result && result.per_tag) || {})[this.tag.id] || {}
      if (own.skipped) {
        this.scanMessageType = 'warning'
        this.scanMessage = `Not scanned: ${own.skipped}.`
        return
      }
      const platforms = own.platforms || {}
      const failed = Object.keys(platforms).filter((p) => platforms[p].status === 'transport_failed')
      if (!result.ok) {
        this.scanMessageType = 'error'
        this.scanMessage = result.message || 'The scan could not store its results.'
      } else if (failed.length && failed.length === Object.keys(platforms).length) {
        this.scanMessageType = 'error'
        const why = (platforms[failed[0]].conflicts || [])[0]
        this.scanMessage = `The registry could not be read, nothing was stored.${why ? ' ' + why : ''}`
      } else if (failed.length) {
        this.scanMessageType = 'warning'
        this.scanMessage = `Scanned, but ${failed.join(', ')} could not be read.`
      } else {
        this.scanMessageType = 'success'
        this.scanMessage = `Scanned: ${result.written || 0} platform result(s) stored.`
      }
    },

    async fetchAllDevices () {
      try {
        const res = await apiService.get('admin/get-docker-sources')
        // categories_all, not categories: an override needs to be able to name chipset 0,
        // which covers both x86 workstations and DGX Spark.
        this.allDevicesAll = res.data.data.categories_all || res.data.data.categories || []
      } catch (e) {
        this.allDevicesAll = []
      }
    },

    /**
     * Persist the override, if the admin touched it.
     *
     * The reason is optional; the backend records who set the override when it is left empty.
     */
    async saveOverride (tagId) {
      if (!this.overrideDirty) { return }
      await apiService.post('admin/tag-compat-overrides', {
        tag_id: tagId,
        chipset_ids_add: this.overrideAdd,
        chipset_ids_deny: this.overrideDeny,
        visibility_override: this.overrideVisibility,
        reason: this.overrideReason
      })
      this.hasOverride = true
    },

    async removeOverride () {
      if (!this.tag || !this.tag.id) { return }
      await apiService.delete(`admin/tag-compat-overrides/${this.tag.id}`)
      this.hasOverride = false
      this.overrideAdd = []
      this.overrideDeny = []
      this.overrideVisibility = null
      this.overrideReason = ''
      Swal.fire({ icon: 'success', title: 'Override removed', timer: 1200, showConfirmButton: false })
    },

    getCurrentDate () {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0') // Aylar 0-11 arası olduğu için 1 ekliyoruz
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },
    updateSelectedVersions (event) {
      // Prevent to select two same label with different versions ex: Ubuntu 11.04 and Ubuntu 18.04
      const lastAdded = event[event.length - 1].split(' ')[0]
      for (const index in event.slice(0, event.length - 1)) {
        if (Object.hasOwnProperty.call(event, index)) {
          const versionLabel = event[index].split(' ')[0]

          if (versionLabel === lastAdded) {
            event.splice(index, 1)
            break
          }
        }
      }
      this.selectedVersions = event
      this.$emit('updateSelectedVersions', event)
    },

    rulesDriverVersion (v) {
      if (!v) {
        return 'Field is required'
      }

      var regExp = /[a-zA-Z]/g
      if (regExp.test(v)) {
        return 'Field input must be number'
      } else {
        return true
      }
    },

    fetchPropByParentID (id) {
      var prop = _.filter(this.propList, (prop) => prop.parent_id === id)
      if (prop.length > 0) {
        return prop[0]
      }
      return null
    },
    // Saving never clears the other arch's fields any more: the radio is derived from stored
    // data when the dialog opens, and a wrong guess used to silently wipe a Jetson tag's
    // devices/JetPacks the first time anyone pressed Save. The `radios` watcher clears them
    // only when an admin deliberately switches arch.
    saveData: async function () {
      if (this.$refs.tagForm.validate()) {
        // Compose validation
        if (this.isCompose) {
          if (this.composeServices.length === 0) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'At least one compose service is required.' })
            return
          }
          for (const svc of this.composeServices) {
            if (!svc.name || (!svc.image && !svc.buildContext)) {
              Swal.fire({ icon: 'error', title: 'Error', text: 'All services must have a name and either an image or a build context.' })
              return
            }
          }
          const names = this.composeServices.map(s => s.name)
          if (new Set(names).size !== names.length) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Service names must be unique.' })
            return
          }
        }

        this.loading = true
        const data = {
          form: {
            name: this.changedTagName,
            label: this.tagLabel,
            devices: this.selectedDevices,
            uncompressed_size: parseInt(this.uncompressedSize),
            jetpacks: this.changedJetpacks,
            min_driver_version: this.minDriverVersion,
            cuda_major: this.cudaMajor,
            tagId: null,
            created_at: new Date(this.createdAt).getTime(),
            // devices / jetpacks / min_driver_version stay in the payload but are no longer
            // filled in by this form: they are derived from tag_image_facts by
            // refresh_compat_cache. Sent empty, tag_update preserves what is stored and
            // tag_new falls back to the workstation chipset -- never an empty list, which the
            // read path would treat as "no filter" and show on every device.
            // is_spark is gone entirely: it is computed from the image's arm64 build.
            compose_yaml: this.isCompose ? this.buildComposeYaml() : null,
            primary_service_name: this.isCompose && this.composeServices.length > 0
              ? this.composeServices[this.primaryServiceIndex]?.name || null
              : null,
            compose_variables: this.isCompose ? this.composeVariables.filter(v => v.key) : null,
            compose_repo_url: this.isCompose && this.composeRepoUrl ? this.composeRepoUrl : null,
            compose_repo_branch: this.isCompose && this.composeRepoBranch ? this.composeRepoBranch : null,
            compose_repo_path: this.isCompose && this.composeRepoPath ? this.composeRepoPath : null,
            compose_repo_filename: this.isCompose && this.composeRepoFilename ? this.composeRepoFilename : null,
            compose_profiles: this.isCompose && Object.keys(this.composeProfiles).length > 0 ? this.composeProfiles : null
          },
          props: this.changedSelectedVersions,
          dockerId: this.dockerId
        }
        if (this.update) {
          data.form.tagId = this.tag.id
        }
        // Sequential try/catch rather than .then/.catch/.finally: the previous version fired a
        // success toast from .finally(), so a failed save showed an error and then "Success",
        // and closed the dialog either way -- taking the admin's unsaved input with it.
        try {
          const res = await apiService.post(
            `admin/docker-tag-${this.update ? 'update' : 'new'}`, data
          )
          // tag_new answers 200 with ok:false on failure; do not report that as created.
          if (res.data && res.data.ok === false) {
            throw new Error(res.data.message || 'The tag could not be saved.')
          }
          // On creation the override is saved against the id tag_new just returned, so an
          // exception can be recorded in the same step instead of "save, reopen, save again".
          const savedId = this.update ? (this.tag && this.tag.id) : (res.data && res.data.id)
          if (savedId) {
            await this.saveOverride(savedId)
          }
          this.$emit('tag-updated')
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: this.update ? 'Tag has been updated' : 'Tag has been created'
          })
          this.$emit('close')
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: 'Error occurred while saving the tag.',
            text: (err.response && err.response.data && err.response.data.message) || err.message
          })
          // Dialog stays open so the admin can correct the input instead of retyping it.
        } finally {
          this.loading = false
        }
      }
    },
    cancel: function () {
      this.$emit('close', false)
    },

    // === Compose Builder Methods ===
    applyRawYaml () {
      this.rawYamlError = null
      try {
        const parsed = yaml.load(this.rawYamlText)
        if (!parsed || typeof parsed !== 'object') {
          this.rawYamlError = 'Invalid YAML: could not parse content.'
          return
        }
        // Support both root-level services and full compose file
        const services = parsed.services || parsed
        if (typeof services !== 'object' || Array.isArray(services)) {
          this.rawYamlError = 'Invalid compose format: expected a "services" object.'
          return
        }
        // Preserve top-level keys (volumes, networks, configs, secrets, etc.)
        if (parsed.services) {
          const rest = { ...parsed }
          delete rest.services
          delete rest.version
          this.composeTopLevelKeys = rest
        } else {
          this.composeTopLevelKeys = {}
        }
        this.loadComposeYaml({ services })
        if (this.composeServices.length === 0) {
          this.rawYamlError = 'No services found in the YAML.'
          return
        }
        this.composeInputMode = 'visual'
        Swal.fire({
          icon: 'success',
          title: 'YAML Applied',
          text: `${this.composeServices.length} service(s) loaded successfully.`,
          timer: 2000,
          showConfirmButton: false
        })
      } catch (e) {
        this.rawYamlError = `YAML parse error: ${e.message}`
      }
    },
    syncRawYamlFromServices () {
      if (this.composeServices.length === 0) {
        this.rawYamlText = ''
        return
      }
      const composeObj = this.buildComposeYaml()
      this.rawYamlText = yaml.dump(composeObj, { indent: 2, lineWidth: -1 })
    },
    addComposeVariable () {
      this.composeVariables.push({
        key: '',
        label: '',
        type: 'text',
        default: '',
        required: false,
        services: []
      })
    },
    createEmptyService () {
      return {
        name: '',
        image: '',
        buildContext: '',
        buildDockerfile: '',
        command: '',
        entrypoint: '',
        restart: 'no',
        depends_on: [],
        networks: '',
        cpus: '',
        memory: '',
        environment: [],
        ports: [],
        volumes: [],
        profiles: [],
        profilesText: '',
        healthcheck: { test: '', interval: '', timeout: '', retries: '' }
      }
    },
    addComposeService () {
      this.composeServices.push(this.createEmptyService())
      this.expandedServicePanel = this.composeServices.length - 1
    },
    syncServiceProfiles (service) {
      service.profiles = (service.profilesText || '').split(',').map(s => s.trim()).filter(s => s)
    },
    removeComposeService (idx) {
      this.composeServices.splice(idx, 1)
      if (this.primaryServiceIndex >= this.composeServices.length) {
        this.primaryServiceIndex = Math.max(0, this.composeServices.length - 1)
      }
      // Update depends_on references
      this.composeServices.forEach(svc => {
        svc.depends_on = svc.depends_on.filter(name =>
          this.composeServices.some(s => s.name === name)
        )
      })
    },
    addEnvVar (service) {
      service.environment.push({ key: '', value: '' })
    },
    addPort (service) {
      service.ports.push({ host: '', container: '' })
    },
    addVolume (service) {
      service.volumes.push({ host: '', container: '' })
    },
    getOtherServiceNames (currentIdx) {
      return this.composeServices
        .filter((_, idx) => idx !== currentIdx)
        .map(s => s.name)
        .filter(n => n)
    },
    buildComposeYaml () {
      const services = {}
      for (const svc of this.composeServices) {
        const definition = {}
        if (svc.image) definition.image = svc.image
        if (svc.buildContext) {
          definition.build = { context: svc.buildContext }
          if (svc.buildDockerfile) definition.build.dockerfile = svc.buildDockerfile
        }
        if (svc.command) definition.command = svc.command
        if (svc.entrypoint) definition.entrypoint = svc.entrypoint
        if (svc.restart && svc.restart !== 'no') definition.restart = svc.restart
        if (svc.depends_on && svc.depends_on.length > 0) definition.depends_on = svc.depends_on
        if (svc.networks) {
          definition.networks = svc.networks.split(',').map(n => n.trim()).filter(n => n)
        }
        if (svc.environment.length > 0) {
          definition.environment = {}
          svc.environment.forEach(env => {
            if (env.key) definition.environment[env.key] = env.value || ''
          })
        }
        if (svc.ports.length > 0) {
          definition.ports = svc.ports
            .filter(p => p.host && p.container)
            .map(p => `${p.host}:${p.container}`)
        }
        if (svc.volumes.length > 0) {
          definition.volumes = svc.volumes
            .filter(v => v.host && v.container)
            .map(v => `${v.host}:${v.container}`)
        }
        if (svc.cpus || svc.memory) {
          definition.deploy = { resources: { limits: {} } }
          if (svc.cpus) definition.deploy.resources.limits.cpus = svc.cpus
          if (svc.memory) definition.deploy.resources.limits.memory = svc.memory
        }
        if (svc.healthcheck && svc.healthcheck.test) {
          definition.healthcheck = {
            test: svc.healthcheck.test,
            ...(svc.healthcheck.interval && { interval: svc.healthcheck.interval }),
            ...(svc.healthcheck.timeout && { timeout: svc.healthcheck.timeout }),
            ...(svc.healthcheck.retries && { retries: parseInt(svc.healthcheck.retries) || 3 })
          }
        }
        if (Array.isArray(svc.profiles) && svc.profiles.length > 0) {
          definition.profiles = svc.profiles
        }
        services[svc.name] = definition
      }
      // Rebuild compose_profiles mapping from service definitions
      const profiles = {}
      for (const [svcName, svcDef] of Object.entries(services)) {
        if (Array.isArray(svcDef.profiles)) {
          for (const p of svcDef.profiles) {
            if (!profiles[p]) profiles[p] = []
            profiles[p].push(svcName)
          }
        }
      }
      this.composeProfiles = profiles
      // Auto-detect named volumes and ensure they are declared at top level
      const result = { services, ...this.composeTopLevelKeys }
      const usedNamedVolumes = new Set()
      for (const svcDef of Object.values(services)) {
        if (!Array.isArray(svcDef.volumes)) continue
        for (const vol of svcDef.volumes) {
          const source = typeof vol === 'string' ? vol.split(':')[0] : null
          if (source && !/^[/.~$]/.test(source)) usedNamedVolumes.add(source)
        }
      }
      if (usedNamedVolumes.size > 0) {
        if (!result.volumes) result.volumes = {}
        for (const name of usedNamedVolumes) {
          if (!result.volumes[name]) result.volumes[name] = null
        }
      }
      return result
    },
    loadComposeYaml (composeYaml) {
      this.composeServices = []
      this.composeProfiles = {}
      if (!composeYaml || !composeYaml.services) return
      const serviceEntries = Object.entries(composeYaml.services)
      serviceEntries.forEach(([name, def]) => {
        const svc = this.createEmptyService()
        svc.name = name
        svc.image = def.image || ''
        // Parse build context
        if (def.build) {
          if (typeof def.build === 'string') {
            svc.buildContext = def.build
          } else if (typeof def.build === 'object') {
            svc.buildContext = def.build.context || ''
            svc.buildDockerfile = def.build.dockerfile || ''
          }
        }
        svc.command = def.command || ''
        svc.entrypoint = def.entrypoint || ''
        svc.restart = def.restart || 'no'
        svc.depends_on = Array.isArray(def.depends_on) ? def.depends_on : []
        svc.networks = Array.isArray(def.networks) ? def.networks.join(', ') : ''
        svc.profiles = Array.isArray(def.profiles) ? [...def.profiles] : []
        svc.profilesText = svc.profiles.join(', ')
        if (def.environment) {
          if (Array.isArray(def.environment)) {
            svc.environment = def.environment.map(e => {
              const [key, ...rest] = e.split('=')
              return { key, value: rest.join('=') }
            })
          } else {
            svc.environment = Object.entries(def.environment).map(([key, value]) => ({ key, value: String(value) }))
          }
        }
        if (def.ports) {
          svc.ports = def.ports.map(p => {
            const parts = String(p).split(':')
            return { host: parts[0], container: parts[1] || parts[0] }
          })
        }
        if (def.volumes) {
          svc.volumes = def.volumes.map(v => {
            const parts = String(v).split(':')
            return { host: parts[0], container: parts[1] || parts[0] }
          })
        }
        if (def.deploy && def.deploy.resources && def.deploy.resources.limits) {
          svc.cpus = def.deploy.resources.limits.cpus || ''
          svc.memory = def.deploy.resources.limits.memory || ''
        }
        if (def.healthcheck) {
          svc.healthcheck = {
            test: def.healthcheck.test || '',
            interval: def.healthcheck.interval || '',
            timeout: def.healthcheck.timeout || '',
            retries: def.healthcheck.retries ? String(def.healthcheck.retries) : ''
          }
        }
        // Extract profiles mapping
        if (svc.profiles.length > 0) {
          for (const p of svc.profiles) {
            if (!this.composeProfiles[p]) this.composeProfiles[p] = []
            this.composeProfiles[p].push(name)
          }
        }
        this.composeServices.push(svc)
      })
    },

    async fetchProps () {
      await apiService.get('admin/docker-props').then((response) => {
        this.propList = response.data.props
      })
    },
    watchGetEndDate () {
      // intentional noop – reserved for future use
    },

    convertDate (timestamp) {
      const convertedDate = new Date(parseInt(timestamp) * 1000)
      const month = convertedDate.getUTCMonth() + 1
      const day = convertedDate.getUTCDate()
      const year = convertedDate.getUTCFullYear()

      const newDateFormat = year + '-' + month + '-' + day
      return newDateFormat
    }
  },
  mounted () {
    this.fetchProps()
    this.jetpacksLoading = true
    apiService.get('admin/get-docker-sources').then((response) => {
      this.allJetpacks = response.data.data.jetpacks
      this.allDevices = response.data.data.categories
    })
  },
  watch: {
    // Only an admin deliberately switching arch clears the other side's fields. Opening the
    // dialog sets `radios` from stored data with settingRadios=true, which this skips.
    radios (newVal, oldVal) {
      if (this.settingRadios || newVal === oldVal) {
        return
      }
      if (newVal === 'radiox86') {
        this.selectedDevices = []
        this.changedJetpacks = []
      } else if (newVal === 'radioJetson') {
        this.minDriverVersion = null
      }
    },
    composeInputMode (newVal) {
      if (newVal === 'raw') {
        this.syncRawYamlFromServices()
        this.rawYamlError = null
      }
    },
    showCreateTagView: {
      handler: function (n) {
        if (n) {
          this.fetchAllDevices()
          this.scanMessage = ''
          if (this.tag) {
            this.update = true
            this.changedTagName = this.tag.name
            this.changedSelectedVersions = this.tag.prop_exp ? this.tag.prop_exp.map((o) => {
              return o.id
            }) : []
            this.changedJetpacks = this.tag.jetpack ? this.tag.jetpack.map((o) => {
              return o.id
            }) : []
            this.tagLabel = this.tag.label
            this.uncompressedSize = this.tag.uncompressed_size
            this.minDriverVersion = this.tag.min_driver_version
            this.cudaMajor = this.tag.cuda_major || null
            this.selectedDevices = this.tag.chipset_ids || []
            if (this.tag.created_at) {
              this.createdAt = this.convertDate(this.tag.created_at)
            } else {
              this.createdAt = this.getCurrentDate()
            }
            // Set radio from what actually makes a tag Jetson-specific -- its JetPacks and its
            // non-workstation chipsets. min_driver_version no longer tells them apart: cuda_major
            // resolves one for Jetson tags too, which made every such tag open as "Server
            // Workstation" with the Devices/Jetpacks fields hidden.
            const hasJetpacks = Array.isArray(this.tag.jetpack) && this.tag.jetpack.length > 0
            const hasJetsonChipset = (this.tag.chipset_ids || []).some((id) => id !== 0)
            this.settingRadios = true
            this.radios = hasJetpacks || hasJetsonChipset ? 'radioJetson' : 'radiox86'
            this.$nextTick(() => { this.settingRadios = false })
            // Load compose data if present
            if (this.tag.compose_yaml) {
              // Preserve top-level keys from stored compose_yaml
              const rest = { ...this.tag.compose_yaml }
              delete rest.services
              delete rest.version
              this.composeTopLevelKeys = rest
              this.loadComposeYaml(this.tag.compose_yaml)
              if (this.tag.primary_service_name) {
                const idx = this.composeServices.findIndex(s => s.name === this.tag.primary_service_name)
                this.primaryServiceIndex = idx >= 0 ? idx : 0
              }
            } else {
              this.composeServices = []
              this.primaryServiceIndex = 0
            }
            // Load compose variables
            if (this.tag.compose_variables && Array.isArray(this.tag.compose_variables)) {
              this.composeVariables = this.tag.compose_variables
            } else {
              this.composeVariables = []
            }
            // Load compose repo fields
            this.composeRepoUrl = this.tag.compose_repo_url || ''
            this.composeRepoBranch = this.tag.compose_repo_branch || ''
            this.composeRepoPath = this.tag.compose_repo_path || ''
            this.composeRepoFilename = this.tag.compose_repo_filename || ''
            // Load compose profiles
            if (this.tag.compose_profiles && typeof this.tag.compose_profiles === 'object') {
              this.composeProfiles = this.tag.compose_profiles
            } else {
              this.composeProfiles = {}
            }
          } else {
            this.update = false
            this.changedTagName = null
            this.tagLabel = null
            this.uncompressedSize = null
            this.minDriverVersion = null
            this.cudaMajor = null
            this.selectedDevices = []
            this.changedJetpacks = []
            this.changedSelectedVersions = []
            this.createdAt = this.getCurrentDate()
            this.settingRadios = true
            this.radios = null
            this.$nextTick(() => { this.settingRadios = false })
            this.composeServices = []
            this.primaryServiceIndex = 0
            this.composeInputMode = 'visual'
            this.rawYamlText = ''
            this.rawYamlError = null
            this.composeVariables = []
            this.composeRepoUrl = ''
            this.composeRepoBranch = ''
            this.composeRepoPath = ''
            this.composeRepoFilename = ''
            const form = this.$refs.tagForm
            if (form) {
              form.reset()
              form.resetValidation()
            }
          }
          // After `update` is settled above: called first, it saw the previous open's value
          // and an edited tag could show the new-tag hint instead of its facts.
          this.fetchCompat()
        }
      },
      immediate: true
    },
    getEndDate: {
      handler (n) {
        this.watchGetEndDate(n)
      }
    }
  },
  computed: {
    // Whether the admin actually touched the override panel. Nothing is written when they
    // did not -- opening a tag must not create an override just by looking at it.
    overrideDirty () {
      return (this.overrideAdd || []).length > 0 ||
        (this.overrideDeny || []).length > 0 ||
        !!this.overrideVisibility
    },

    isCompose () {
      return this.dockerType === 'compose'
    },
    composeServiceNames () {
      return this.composeServices.map(s => s.name).filter(n => n)
    },
    checkJetsonSelected () {
      if (this.selectedDevices.length === 0) return true
    },
    generateAllPossibleVersions: function () {
      const generatedAllPossibleVersionsList = []
      this.propList.forEach((prop) => {
        if (prop.parent_id === -1) {
          this.propList.forEach((childProp) => {
            if (childProp.parent_id === prop.id) {
              generatedAllPossibleVersionsList.push({
                id: childProp.id,
                text: prop.setting_name + ' ' + childProp.setting_name
              })
            }
          })
        }
      })
      return generatedAllPossibleVersionsList
    }
  }
})
</script>

<style>
.inputPrice input[type='number'] {
  -moz-appearance: textfield;
}

.inputPrice input::-webkit-outer-spin-button,
.inputPrice input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>
